import Phaser from 'phaser';

const COMET_DEPTH = 200;
const TRAIL_HISTORY = 10;
const SLASH_LIFE_MS = 220;

export class Comet extends Phaser.GameObjects.Container {
  public readonly collisionRadius = 16;

  private core!: Phaser.GameObjects.Graphics;
  private glow!: Phaser.GameObjects.Graphics;
  private slashGfx!: Phaser.GameObjects.Graphics;
  private trail: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private trailPoints: Array<{ x: number; y: number; t: number }> = [];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);

    this.glow = scene.add.graphics();
    this.glow.fillStyle(0x22d3ee, 0.18);
    this.glow.fillCircle(0, 0, 56);
    this.glow.fillStyle(0x22d3ee, 0.32);
    this.glow.fillCircle(0, 0, 36);

    this.core = scene.add.graphics();
    this.core.fillStyle(0x67e8f9, 0.85);
    this.core.fillCircle(0, 0, 22);
    this.core.fillStyle(0xffffff, 1);
    this.core.fillCircle(0, 0, 14);

    this.add([this.glow, this.core]);
    this.setDepth(COMET_DEPTH);

    this.slashGfx = scene.add.graphics();
    this.slashGfx.setDepth(COMET_DEPTH - 1);

    if (scene.textures.exists('particle')) {
      this.trail = scene.add.particles(x, y, 'particle', {
        follow: this,
        scale: { start: 1.1, end: 0 },
        alpha: { start: 0.85, end: 0 },
        speed: { min: 0, max: 60 },
        lifespan: 300,
        frequency: 10,
        tint: [0x22d3ee, 0xffffff, 0x67e8f9],
        blendMode: Phaser.BlendModes.ADD,
        quantity: 2
      });
      this.trail.setDepth(COMET_DEPTH - 2);
    }

    scene.add.existing(this);
    this.recordPoint(x, y);
  }

  follow(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.recordPoint(x, y);
  }

  tick(): void {
    this.drawSlash();
  }

  private recordPoint(x: number, y: number): void {
    this.trailPoints.push({ x, y, t: this.scene.time.now });
    while (this.trailPoints.length > TRAIL_HISTORY) this.trailPoints.shift();
  }

  drawSlash(): void {
    if (!this.slashGfx || !this.scene) return;
    this.slashGfx.clear();
    if (this.trailPoints.length < 2) return;
    const now = this.scene.time.now;
    for (let i = 1; i < this.trailPoints.length; i++) {
      const p0 = this.trailPoints[i - 1];
      const p1 = this.trailPoints[i];
      const age = now - p1.t;
      const alpha = Math.max(0, 1 - age / SLASH_LIFE_MS);
      if (alpha <= 0) continue;
      const width = Math.max(2, 14 * alpha);
      this.slashGfx.lineStyle(width, 0xffffff, alpha * 0.55);
      this.slashGfx.beginPath();
      this.slashGfx.moveTo(p0.x, p0.y);
      this.slashGfx.lineTo(p1.x, p1.y);
      this.slashGfx.strokePath();
    }
  }

  fadeOut(): void {
    if (this.slashGfx) {
      const sg = this.slashGfx;
      this.scene.tweens.add({
        targets: sg,
        alpha: 0,
        duration: 200,
        onComplete: () => sg.destroy()
      });
    }
    if (this.trail) {
      const e = this.trail;
      this.trail = null;
      e.stop();
      this.scene.time.delayedCall(380, () => e.destroy());
    }
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scale: 0.6,
      duration: 180,
      onComplete: () => super.destroy()
    });
  }

  destroy(fromScene?: boolean): void {
    this.slashGfx?.destroy();
    this.slashGfx = null as unknown as Phaser.GameObjects.Graphics;
    if (this.trail) {
      const e = this.trail;
      this.trail = null;
      e.stop();
      this.scene.time.delayedCall(380, () => e.destroy());
    }
    super.destroy(fromScene);
  }
}
